import { apiRequest } from './client';
import type { SearchDocKind } from '../lib/location';

/**
 * The doc-content search — `GET /api/search/docs` (plan 2.1's endpoint, plan 2.2's caller).
 *
 * Same-origin through the one API client, like everything else: the session cookie rides
 * along, a non-2xx throws, and nothing here can reach outside the deployment (the CSP's
 * `connect-src 'self'` is what makes that enforceable rather than conventional).
 *
 * Match semantics are the CLIENT'S — the route confirms every candidate with the exact
 * `trim().toLowerCase().includes()` the map's `nodeMatchesSearch` runs, so a doc hit and a
 * node hit for the same typed text obey one rule. `q` under two characters answers empty
 * without touching the database; results are capped at 20 with the true `total` beside them.
 */

export interface DocSearchRow {
  docId: string;
  slug: string;
  /** The doc-slot kind ('documentation' | 'internal' | 'course' | 'demo' | 'custom'). */
  kind: string;
  docType: string;
  title: string;
  /** The OWNING node — what the map lights up, and where a click navigates. */
  nodeId: string;
  /** Resolved server-side for the requested locale; the client prefers its own state. */
  nodeLabel: string | null;
  /** Where the match was found. A 'title' hit has no snippet — the title IS the evidence. */
  field: 'title' | 'content';
  /** ~160 chars around the first occurrence, word-snapped. Empty for a title hit. */
  snippet: string;
  /** The match's offsets WITHIN the snippet, so bolding needs no re-search. */
  hlStart: number;
  hlLen: number;
  locale: string;
}

export interface DocSearchResponse {
  results: DocSearchRow[];
  total: number;
}

export function searchDocs(
  q: string,
  opts: { kind?: SearchDocKind | ''; locale?: string } = {},
): Promise<DocSearchResponse> {
  return apiRequest<DocSearchResponse>('/api/search/docs', {
    query: { q, kind: opts.kind || undefined, locale: opts.locale || undefined },
  });
}
